import { AlertTriangle, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { LineSupply } from './supply.api'
import { supplyLabel, supplyTone } from './supply-labels'

const TONE_CLASSES = {
  buy: 'border-slate-200 bg-slate-50 text-slate-900',
  make: 'border-slate-200 bg-white text-slate-900',
  warn: 'border-amber-300 bg-amber-50 text-amber-900',
  none: 'border-transparent bg-transparent text-muted-foreground',
  lock: 'border-slate-200 bg-slate-100 text-slate-700',
} as const

interface SupplyModeChipProps {
  supply: LineSupply
  supplierName?: string | null
  /** Opens the line's supply panel; without it the chip is read-only. */
  onClick?: () => void
  testId?: string
  /** False in a narrow column: the label is what must stay readable, the reference is in the panel. */
  showReference?: boolean
}

/** The Appro cell (SOLY-IMPORT-003 S11): words and an icon, never a colour alone. */
export function SupplyModeChip({ supply, supplierName, onClick, testId, showReference = true }: SupplyModeChipProps) {
  const tone = supplyTone(supply)
  const label = supplyLabel(supply, supplierName)
  const content = (
    <>
      {tone === 'lock' ? <Lock className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {tone === 'warn' ? <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden /> : null}
      <span className="truncate">{label}</span>
      {showReference && supply.mode === 'buy' && supply.supplierReference ? (
        <span className="shrink-0 text-[11px] text-muted-foreground">réf. {supply.supplierReference}</span>
      ) : null}
      {tone === 'lock' ? <span className="shrink-0 text-[11px] text-muted-foreground">· figé</span> : null}
    </>
  )
  const className = cn(
    'inline-flex h-7 max-w-full items-center gap-1.5 rounded-md border px-2 text-xs',
    TONE_CLASSES[tone],
  )

  if (!onClick) {
    return <span className={className} data-testid={testId} data-supply-mode={supply.mode}>{content}</span>
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(className, 'hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
      data-testid={testId}
      data-supply-mode={supply.mode}
      aria-label={`Appro : ${label}. Modifier`}
    >
      {content}
    </button>
  )
}
